import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import {
  getStoredPinHash,
  getIsOperatorActive,
  setOperatorActive,
  storePinHash,
  clearPinStorage,
  hashPin,
  verifyStoredPin,
} from '@/shared/lib/operatorMode'

type OperatorModeContextValue = {
  isOperatorMode: boolean
  hasPinSet: boolean
  setupPin: (pin: string) => Promise<void>
  changePin: (oldPin: string, newPin: string) => Promise<boolean>
  removePinAndDisable: (pin: string) => Promise<boolean>
  enableOperatorMode: (pin: string) => Promise<boolean>
  disableOperatorMode: (pin: string) => Promise<boolean>
}

const Ctx = createContext<OperatorModeContextValue | null>(null)

export function OperatorModeProvider({ children }: { children: ReactNode }) {
  const { org } = useAuth()
  const orgId = org?.id ?? ''

  const [isOperatorMode, setIsOperatorMode] = useState(false)
  const [hasPinSet, setHasPinSet] = useState(false)

  useEffect(() => {
    if (!orgId) {
      setIsOperatorMode(false)
      setHasPinSet(false)
      return
    }
    const pinHash = getStoredPinHash(orgId)
    const active = getIsOperatorActive(orgId)
    // Defensive: an "active" flag with no PIN hash (partial storage loss) would
    // leave the device stuck in operator mode with no PIN to exit. Force owner
    // mode and clear the stale flag. NOTE: a full clear-data wipes both keys and
    // also lands in owner mode — that bypass can only be closed at the device
    // level (Android Screen Pinning / iOS Guided Access), not from client code.
    if (active && !pinHash) {
      setOperatorActive(orgId, false)
      setIsOperatorMode(false)
      setHasPinSet(false)
      return
    }
    setIsOperatorMode(active)
    setHasPinSet(!!pinHash)
  }, [orgId])

  async function setupPin(pin: string): Promise<void> {
    const h = await hashPin(pin)
    storePinHash(orgId, h)
    setHasPinSet(true)
  }

  async function changePin(oldPin: string, newPin: string): Promise<boolean> {
    const ok = await verifyStoredPin(orgId, oldPin)
    if (!ok) return false
    const h = await hashPin(newPin)
    storePinHash(orgId, h)
    return true
  }

  async function removePinAndDisable(pin: string): Promise<boolean> {
    const ok = await verifyStoredPin(orgId, pin)
    if (!ok) return false
    clearPinStorage(orgId)
    setHasPinSet(false)
    setIsOperatorMode(false)
    return true
  }

  async function enableOperatorMode(pin: string): Promise<boolean> {
    const ok = await verifyStoredPin(orgId, pin)
    if (!ok) return false
    setOperatorActive(orgId, true)
    setIsOperatorMode(true)
    return true
  }

  async function disableOperatorMode(pin: string): Promise<boolean> {
    const ok = await verifyStoredPin(orgId, pin)
    if (!ok) return false
    setOperatorActive(orgId, false)
    setIsOperatorMode(false)
    return true
  }

  return (
    <Ctx.Provider value={{
      isOperatorMode,
      hasPinSet,
      setupPin,
      changePin,
      removePinAndDisable,
      enableOperatorMode,
      disableOperatorMode,
    }}>
      {children}
    </Ctx.Provider>
  )
}

export function useOperatorMode() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useOperatorMode must be used inside OperatorModeProvider')
  return ctx
}
